<script setup>
import { Head, Link } from '@inertiajs/vue3';
import { reactive, onMounted, computed } from 'vue';
import { usePage } from '@inertiajs/vue3';
import LeftMenuWrapper from '@/Components/LeftMenu/LeftMenuWrapper.vue';
import ProductPageLayout from '@/Layouts/ProductPageLayout.vue';
import { all } from 'axios';
import PriceGraph from '@/Components/Product/PriceGraph.vue';
import PriceAlertButton from '@/Components/PriceAlert/PriceAlertButton.vue';
import PriceAlertModal from '@/Components/PriceAlert/PriceAlertModal.vue';
import FavoriteButton from '@/Components/Favorite/FavoriteButton.vue';
import BreadcrumbBar from '@/Components/BreadcrumbBar.vue';
import AddToListButton from '@/Components/ProductList/AddToListButton.vue';
import PriceRow from '@/Components/Product/PriceRow.vue';

const page = usePage();

const user = computed(
    () => page.props.auth.user,
);

const isAuthenticated = computed(
    () => page.props.isAuthenticated,
);

const userPriceAlert = computed(
    () => page.props.userPriceAlert,
);

const userFavoriteStatus = computed(
    () => page.props.userFavoriteStatus || false,
);

const productdata = computed(
    () => page.props.product,
);

defineProps({
    canLogin: Boolean,
    canRegister: Boolean,
    laravelVersion: String,
    phpVersion: String,
    menuItems: Array,
});

function numberFormat(value) {
    //convert to num first
    value = Number(value);

    //use intl
    return new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
}

const pageTitle = computed(() => {
    return page.props.pageTitle;
});

const breadcrumbs = computed(
    () => page.props.breadcrumbs
);

const urlForCheapestStore = computed(() => {
    return page.props.productPrices[0].price_guid;
});

const pageUrl = computed(() => {
    return window.location.href;
});

const firstImage = computed(() => {
    return window.location.origin + "/images/" + page.props.product.images[0].id;
});

const chartOptions = {
  chart: {
    type: 'line'
  },
  title: {
    text: 'Monthly Sales Data'
  },
  xAxis: {
    categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  },
  yAxis: {
    title: {
      text: 'Revenue (in USD)'
    }
  },
  series: [
    {
      name: 'Sales',
      data: [29.9, 71.5, 106.4, 129.2, 144.0, 176.0, 135.6, 148.5, 216.4, 194.1, 95.6, 54.4]
    }
  ]
}

const showAlertModal = reactive({
    value: false
});

function openPriceAlertModal() {
    showAlertModal.value = true;
}

function handleFavoriteChanged(newStatus) {
    // Add animation class to favorite button
    const favoriteBtn = document.querySelector('.btn-favorite');
    if (favoriteBtn) {
        favoriteBtn.classList.add('favorite-animation');
        setTimeout(() => {
            favoriteBtn.classList.remove('favorite-animation');
        }, 700); // Remove class after animation completes
    }
}
</script>

<template>
    <ProductPageLayout>
        <div class="container-fluid product-detail-wrapper">
            <BreadcrumbBar :breadcrumbs="breadcrumbs" />
            <div class="row p-0">
                <div class="col-sm-12 col-md-10">
                    <div class="container-fluid">
                        <div class="row design-row">
                            <div class="col-md-3">
                                <div class="card">
                                    <div class="card-body">
                                        <template v-if="page.props.product.images.length > 0">
                                            <div v-for="(image, index) in page.props.product.images" :key="index" :class="{ 'carousel-item': index == 0, 'carousel-item': index > 0 }">
                                                <a :href="`/images/${image.id}`" data-lightbox="product-images" data-title="{{ image.name }}">
                                                    <img :src="`/images/${image.id}`" class="d-block w-100" alt="{{ image.name }}">
                                                </a>
                                            </div>
                                        </template>
                                        <template v-else>
                                            <div class="carousel-item active">
                                                <img :src="`/images/static/placeholder.png`" class="d-block w-100" alt="Placeholder">
                                            </div>
                                        </template>
                                    </div>
                                </div>
                            </div>
                            <div class="col-md-9">
                                <div class="card shadow-sm">
                                    <div class="card-body">
                                        <div class="modern-product-card p-4">
                                            <h4 class="product-title mb-3 d-flex justify-content-between align-items-center">
                                                <span>{{ page.props.product.model_short_name }}</span>
                                                <Link :href="route('brands.show', page.props.product.brand.id)">
                                                    <img :src="`/brand-images/${page.props.product.brand.id}`" :alt="`${ page.props.product.brand.name }`" class="brand-logo" />
                                                </Link>
                                            </h4>
                                            
                                            <div v-if="productdata.attribute_texts" class="product-attribute-texts mb-3">
                                                <span v-for="(attribute, index) in productdata.attribute_texts" :key="index">{{ attribute }}</span>
                                            </div>
                                            
                                            <p class="text-muted mb-4" v-if="productdata.model_short_name">
                                                Sammenlign priser på {{ productdata.model_short_name }} fra 
                                                <strong>{{ numberFormat(productdata.prices.lowest_price_in_stock_including_shipping) }}</strong> kr. til 
                                                <strong>{{ numberFormat(productdata.prices.highest_price_including_shipping) }}</strong> kr. fra 
                                                <strong>{{ productdata.prices.total_retailers }}</strong> forhandler(e).
                                            </p>

                                            <div class="price-section mb-4 p-3 bg-light rounded">
                                                <div class="best-price">
                                                    <h3 class="mb-0 text-primary">{{ numberFormat(productdata.prices.lowest_price_in_stock_including_shipping) }}</h3>
                                                    <span class="text-muted small">INKL. FRAGT</span>
                                                </div>
                                            </div>

                                            <div class="actions-row d-flex gap-2 align-items-center">
                                                <a v-if="urlForCheapestStore" 
                                                   :href="`/redirect/${page.props.product.pricecompare_id}/${urlForCheapestStore}`" 
                                                   class="btn btn-pricecompare-primary">
                                                    <font-awesome-icon :icon="['fas', 'shop']" class="me-2" />
                                                    Gå til billigste butik
                                                </a>
                                                
                                                <PriceAlertButton 
                                                    :has-alert="!!page.props.userPriceAlert"
                                                    @open-modal="openPriceAlertModal"
                                                />
                                                
                                                <FavoriteButton 
                                                    :product-id="page.props.product.pricecompare_id"
                                                    :initial-favorite-status="userFavoriteStatus"
                                                    @favorite-changed="handleFavoriteChanged"
                                                />
                                                
                                                <AddToListButton
                                                    :product-id="page.props.product.pricecompare_id"
                                                    :is-authenticated="isAuthenticated"
                                                />
                                                
                                                <PriceAlertModal
                                                    :show="showAlertModal.value"
                                                    :product-id="page.props.product.pricecompare_id"
                                                    :is-authenticated="isAuthenticated"
                                                    :existing-alert="userPriceAlert"
                                                    @close="showAlertModal.value = false"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="row design-row">
                            <div class="col-md-12">
                                <ul class="nav nav-tabs">
                                    <li class="nav-item">
                                        <a class="nav-link active" data-bs-toggle="tab" href="#priser">Priser</a>
                                    </li>
                                    <li class="nav-item">
                                        <a class="nav-link" data-bs-toggle="tab" href="#refurb-priser">Refurb priser</a>
                                    </li>
                                </ul>
                                <div class="tab-content" id="productTabsContent">
                                    <div class="tab-pane fade show active" id="priser">
                                        <div class="card">
                                            <div class="card-body p-0">
                                                <template v-for="(price, index) in page.props.productPrices" :key="index">
                                                    <PriceRow 
                                                        :price="price" 
                                                        :index="index" 
                                                        :productId="String(page.props.product.pricecompare_id)" 
                                                        class="mb-3"
                                                    />
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="tab-pane fade" id="refurb-priser">
                                        <div class="card">
                                            <div class="card-body">
                                                Ingen priser på refurbished eller demo modeller fundet.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <br/>
                        <div>
                            <div class="card">
                                <div class="card-body">
                                    <PriceGraph :options="chartOptions" :productId="page.props.product.pricecompare_id" />
                                </div>
                            </div>
                        </div>
                        <br/>
                        <div class="row data-row">
                            <div class="col-md-12">
                                <ul class="nav nav-tabs">
                                    <li class="nav-item">
                                        <a class="nav-link active" data-bs-toggle="tab" href="#specs">Specifikationer</a>
                                    </li>
                                    <li class="nav-item">
                                        <a class="nav-link" data-bs-toggle="tab" href="#productinfo">Produktinfo</a>
                                    </li>
                                </ul>
                                <div class="tab-content" id="productTabsContent">
                                    <div class="tab-pane fade show active" id="specs">
                                        <div class="card">
                                            <div class="card-body">
                                                <div class="table-responsive">
                                                    <div class="d-flex flex-column">
                                                        <div class="d-none d-md-flex bg-light fw-bold">
                                                            <div class="p-2 flex-fill">Specifikation</div>
                                                            <div class="p-2 flex-fill text-end">Værdi</div>
                                                        </div>
                                                        <template v-for="(group, index) in page.props.product.attributes" :key="index">
                                                            <div class="bg-secondary text-white p-2">{{ group.group_name }}</div>
                                                            <template v-for="(specification, index) in group.attributes" :key="index">
                                                                <div class="d-flex flex-column flex-md-row border-bottom">
                                                                    <div class="p-2 flex-fill fw-bold">{{ specification.name }}</div>
                                                                    <div class="p-2 flex-fill text-end">{{ Array.isArray(specification.value) ? specification.value.join(', ') : specification.value }}</div>
                                                                </div>
                                                            </template>
                                                        </template>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="tab-pane fade" id="productinfo">
                                        <div class="card">
                                            <div class="card-body">
                                                Vi har desværre ikke yderligere information om dette produkt.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            &nbsp;
                        </div>
                    </div>
                </div>
                <div class="col-sm-0 col-md-2">
                    <div class="rightmenu-wrapper">
                        <div class="row secondary-row">
                            <div class="col-sm-0 col-md-12">
                                <div class="card">
                                    <div class="card-body">
                                        Box 1
                                    </div>
                                </div>
                            </div>
                            <div class="col-sm-0 col-md-12"><br/></div>
                            <div class="col-sm-0 col-md-12">
                                <div class="card">
                                    <div class="card-body">
                                        Box 1
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </ProductPageLayout>
</template>


<style scoped>
.product-detail {
    display: flex;
    align-items: center;
}

.product-image {
    flex: 1;
}

.product-info {
    flex: 1;
}

.modern-product-card {
    border-radius: 8px;
}

.product-title {
    font-weight: 600;
    color: #333;
}

.price-section {
    border-left: 4px solid #1E3A8A;
}

.best-price {
    display: flex;
    flex-direction: column;
}

.best-price h3 {
    color: #1E3A8A;
    font-weight: 700;
    font-size: 1rem;
}

.actions-row {
    margin-top: 1.5rem;
}

.design-row {
    margin-top: 1rem;
}

.secondary-row {
    margin-top: 1rem;
}

.nav-tabs {
    border-bottom: none;
    background-color: transparent;
    border-radius: 0.5rem 0.5rem 0 0;
    overflow: hidden;
    justify-content: flex-start;
}

.nav-tabs .nav-item {
    text-align: center;
    margin-right: 0.5rem;
}

.nav-tabs .nav-link {
    border: none;
    border-radius: 0.5rem 0.5rem 0 0;
    color: #0d6efd;
    font-weight: 500;
    padding: 0.5rem 1rem;
    background-color: #e9ecef;
    transition: background-color 0.3s ease;
    white-space: nowrap;
}

.nav-tabs .nav-link.active {
    background-color: #fff;
    color: #0d6efd;
    font-weight: bold;
}

.nav-tabs .nav-link:hover {
    background-color: #dee2e6;
}

.tab-content {
    border: 1px solid #dee2e6;
    border-top: none;
    padding: 1rem;
    background-color: #fff;
    border-radius: 0 0 0.5rem 0.5rem;
}

.rightmenu-wrapper {
    height: 100%;
}

.product-detail-wrapper {
    height: 100%;
}

.price-label span {
    font-size: 0.8em;
    color: #6c757d;
}

.breadcrumb-wrapper {
    padding-left: 1.5rem;
    height: auto;
}

.breadcrumb-wrapper ol {
    padding: 0.1rem 0.1rem;
    margin-bottom: 0;
}

.breadcrumb-wrapper ol li a {
    text-decoration: none;
    color: #1E3A8A;
    font-weight: bold;
    text-align: center;
    text-transform: uppercase;
    font-size: 0.8rem;
}

.expander-wrapper {
    height: 1.5rem;
}

.expander-wrapper:hover {
    cursor: pointer;
}

.favorite-animation {
    animation: heartBeat 0.7s ease-in-out;
    transform-origin: center;
}

@keyframes heartBeat {
    0% {
        transform: scale(1);
    }
    14% {
        transform: scale(1.3);
    }
    28% {
        transform: scale(1);
    }
    42% {
        transform: scale(1.3);
    }
    70% {
        transform: scale(1);
    }
}

.compact-price-row {
    padding: 0.5rem;
    border: 1px solid #dee2e6;
    border-radius: 0.25rem;
}

.retailer-logo img {
    width: 40px;
    height: 40px;
}

.stock-info {
    font-size: 0.9rem;
}

.lead-time {
    font-size: 0.8rem;
    color: #6c757d;
}

.price-info {
    text-align: right;
}

.price-info h5 {
    font-size: 1rem;
    margin-bottom: 0;
}

.action-link .btn {
    font-size: 0.8rem;
    padding: 0.25rem 0.5rem;
}

.brand-logo {
    max-height: 30px;
    object-fit: contain;
}

@media (max-width: 576px) {
    .headers {
        display: none;
    }

    .tab-content {
        padding: 0.5rem;
    }

    .tab-pane {
        padding: 0.5rem;
    }

    .card {
        margin-bottom: 1rem;
    }

    .table {
        font-size: 0.8rem;
    }

    .table th, 
    .table td {
        padding: 0.5rem;
    }

    .nav-tabs {
        flex-wrap: wrap;
    }

    .nav-tabs .nav-item {
        flex: 1 1 50%;
        text-align: center;
    }

    .nav-tabs .nav-link {
        font-size: 0.9rem;
        padding: 0.5rem;
    }

    .tab-content {
        font-size: 0.9rem;
    }
}
</style>